// Stripe checkout, customer portal, and subscription state — all server fns.
// Auth model: the client passes its Supabase access token in the input payload
// (TanStack's useServerFn does not auto-attach Authorization headers). We then
// validate it server-side using the publishable key.
//
// SECURITY: All server-only modules (`@/integrations/supabase/client.server`,
// `@/server/stripe/stripe.server`, `getRequestHeader`, etc.) are kept behind
// `./billing.server` and only imported INSIDE `.handler()` bodies via dynamic
// import. The TanStack server-fn Vite plugin strips handler bodies from the
// client bundle, but top-level imports of a `.functions.ts` file ship to the
// client — so we never put service-role or Stripe imports at module scope.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { PLANS, type PlanId, tierFromPriceId, resolveSubscriptionTier } from "@/lib/plans";

// ─── Create Checkout Session ────────────────────────────────────────────────
export const createCheckoutSession = createServerFn({ method: "POST" })
  .inputValidator((data) =>
    z
      .object({
        plan: z.enum(["monthly", "annual"]),
        accessToken: z.string().min(10),
        withTrial: z.boolean().optional().default(false),
        // "choose-plan" = v2 card-required signup; "settings" = legacy
        // BillingModal upgrade flow. Determines card collection + return URLs.
        flow: z.enum(["choose-plan", "settings"]).optional().default("settings"),
        // Pre-applied promo code captured via ?promo=. Validated against
        // Stripe; invalid codes are silently ignored so user can enter at checkout.
        promoCode: z.string().min(1).max(50).optional(),
        // First-touch UTM parameters captured via sessionStorage on landing.
        // Forwarded to Stripe customer + subscription + checkout session
        // metadata for marketing attribution. All keys optional.
        utm: z
          .object({
            utm_source: z.string().max(200).optional(),
            utm_medium: z.string().max(200).optional(),
            utm_campaign: z.string().max(200).optional(),
            utm_content: z.string().max(200).optional(),
            utm_term: z.string().max(200).optional(),
          })
          .optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { authFromToken, getOrCreateCustomerId, siteOrigin, stripe, supabaseAdmin } =
      await import("./billing.server");

    const { userId, email } = await authFromToken(data.accessToken);
    const plan = PLANS[data.plan as PlanId];

    const customerId = await getOrCreateCustomerId(userId, email);
    const origin = siteOrigin();
    const isChoosePlan = data.flow === "choose-plan";

    // v2 card-required flow: always 7-day Stripe trial.
    // Legacy "settings" flow: compute remaining in-app trial days as before.
    let trialDaysForStripe: number | null = null;
    if (data.withTrial) {
      if (isChoosePlan) {
        trialDaysForStripe = 7;
      } else {
        const { data: profile } = await supabaseAdmin
          .from("profiles")
          .select("trial_ends_at")
          .eq("id", userId)
          .maybeSingle();
        if (profile?.trial_ends_at) {
          const ms = new Date(profile.trial_ends_at).getTime() - Date.now();
          const days = Math.ceil(ms / 86400000);
          trialDaysForStripe = Math.max(1, Math.min(7, days));
        } else {
          trialDaysForStripe = 7;
        }
      }
    }

    // If the user already has a trialing/active Stripe subscription, switch
    // the price on it instead of opening a new checkout (preserves trial).
    const existing = await stripe().subscriptions.list({
      customer: customerId,
      status: "all",
      limit: 5,
    });
    const liveSub = existing.data.find((s) =>
      ["trialing", "active", "past_due"].includes(s.status),
    );
    if (liveSub) {
      const item = liveSub.items.data[0];
      await stripe().subscriptions.update(liveSub.id, {
        items: [{ id: item.id, price: plan.priceId }],
        proration_behavior: "none",
        metadata: { supabase_user_id: userId, plan: plan.id },
      });
      if (!data.withTrial && liveSub.status === "trialing") {
        await stripe().subscriptions.update(liveSub.id, { trial_end: "now" });
      }
      return { url: `${origin}/app?upgrade=success` };
    }

    // Validate optional promo code against Stripe. Silently ignore invalid.
    // NOTE: For users with a pending referral, the referral coupon takes
    // precedence at first-paid-invoice time (see referrals.server.ts) —
    // Stripe only applies one coupon per invoice, and the referral $14.99
    // credit is the higher-value reward for the user. The signup promo is
    // still persisted on the profile for marketing attribution.
    let stripeDiscounts: Array<{ promotion_code: string }> | undefined;
    if (data.promoCode) {
      try {
        const pc = await stripe().promotionCodes.list({
          code: data.promoCode.toUpperCase(),
          active: true,
          limit: 1,
        });
        if (pc.data[0]?.id) {
          stripeDiscounts = [{ promotion_code: pc.data[0].id }];
        }
      } catch (e) {
        console.warn("[billing] promo validation failed, ignoring", e);
      }
    }

    const successUrl = isChoosePlan
      ? `${origin}/app?upgrade=success&new_signup=1&session_id={CHECKOUT_SESSION_ID}`
      : `${origin}/app?upgrade=success&session_id={CHECKOUT_SESSION_ID}`;
    const cancelUrl = isChoosePlan
      ? `${origin}/choose-plan?cancelled=1`
      : `${origin}/app?upgrade=cancelled`;

    const session = await stripe().checkout.sessions.create({
      customer: customerId,
      mode: "subscription",
      line_items: [{ price: plan.priceId, quantity: 1 }],
      // If a promo is pre-applied via discounts[], Stripe disallows
      // allow_promotion_codes (one or the other). Otherwise let users enter
      // a code manually in the Checkout UI.
      ...(stripeDiscounts
        ? { discounts: stripeDiscounts }
        : { allow_promotion_codes: true }),
      // v2 signup MUST collect a card (Netflix-style). Legacy flow stays "if_required".
      payment_method_collection: isChoosePlan ? "always" : "if_required",
      success_url: successUrl,
      cancel_url: cancelUrl,
      subscription_data: {
        ...(trialDaysForStripe !== null
          ? { trial_period_days: trialDaysForStripe }
          : {}),
        metadata: {
          supabase_user_id: userId,
          plan: plan.id,
          signup_flow: data.flow,
          ...(data.promoCode ? { signup_promo_code: data.promoCode.toUpperCase() } : {}),
          ...(data.utm ?? {}),
        },
      },
      metadata: {
        supabase_user_id: userId,
        plan: plan.id,
        with_trial: String(data.withTrial),
        signup_flow: data.flow,
        ...(data.promoCode ? { signup_promo_code: data.promoCode.toUpperCase() } : {}),
        ...(data.utm ?? {}),
      },
    });

    // Also stamp UTMs on the Stripe customer itself so attribution sticks
    // even if the subscription is recreated later (e.g. cancel + resubscribe).
    if (data.utm && Object.keys(data.utm).length > 0) {
      try {
        await stripe().customers.update(customerId, { metadata: { ...data.utm } });
      } catch (e) {
        console.warn("[billing] failed to stamp UTMs on customer, ignoring", e);
      }
    }


    // If skipping trial, clear the in-app trial window so the UI stops
    // showing "trial day X of 7".
    if (!data.withTrial) {
      await supabaseAdmin
        .from("profiles")
        .update({ trial_ends_at: new Date().toISOString() })
        .eq("id", userId);
    }

    if (!session.url) throw new Error("Stripe did not return a checkout URL");
    return { url: session.url };
  });

// ─── Cancel In-App Trial ────────────────────────────────────────────────────
// Used by the "Cancel trial" button in the Me tab. Cancels any live Stripe
// subscription and revokes in-app trial access immediately.
export const cancelTrialSubscription = createServerFn({ method: "POST" })
  .inputValidator((data) =>
    z.object({ accessToken: z.string().min(10) }).parse(data),
  )
  .handler(async ({ data }) => {
    const { authFromToken, getOrCreateCustomerId, stripe, supabaseAdmin } =
      await import("./billing.server");

    const { userId, email } = await authFromToken(data.accessToken);
    const customerId = await getOrCreateCustomerId(userId, email);

    // Cancel any active/trialing Stripe subscriptions immediately.
    const subs = await stripe().subscriptions.list({
      customer: customerId,
      status: "all",
      limit: 10,
    });
    for (const s of subs.data) {
      if (["trialing", "active", "past_due", "incomplete"].includes(s.status)) {
        try {
          await stripe().subscriptions.cancel(s.id);
        } catch (e) {
          console.error("[billing] cancel sub failed", s.id, e);
        }
      }
    }

    // Revoke in-app trial: set trial_ends_at to now and clear premium flags.
    await supabaseAdmin
      .from("profiles")
      .update({
        is_premium: false,
        subscription_status: "canceled",
        cancel_at_period_end: false,
        trial_ends_at: new Date().toISOString(),
      })
      .eq("id", userId);

    return { ok: true };
  });

// ─── Create Customer Portal Session ─────────────────────────────────────────
export const createCustomerPortalSession = createServerFn({ method: "POST" })
  .inputValidator((data) =>
    z.object({ accessToken: z.string().min(10) }).parse(data),
  )
  .handler(async ({ data }) => {
    const { authFromToken, getOrCreateCustomerId, siteOrigin, stripe } =
      await import("./billing.server");

    const { userId, email } = await authFromToken(data.accessToken);

    const customerId = await getOrCreateCustomerId(userId, email);
    const origin = siteOrigin();

    const portal = await stripe().billingPortal.sessions.create({
      customer: customerId,
      return_url: `${origin}/app?from=portal`,
    });

    return { url: portal.url };
  });

// ─── Get Subscription State ─────────────────────────────────────────────────
// Cheap read of the cached state on the profile (kept fresh by webhook).
export const getSubscriptionState = createServerFn({ method: "POST" })
  .inputValidator((data) =>
    z.object({ accessToken: z.string().min(10) }).parse(data),
  )
  .handler(async ({ data }) => {
    const { authFromToken, supabaseAdmin } = await import("./billing.server");

    const { userId } = await authFromToken(data.accessToken);
    const { data: profile } = await supabaseAdmin
      .from("profiles")
      .select(
        "is_premium, is_test_user, is_launch_comp, subscription_status, subscription_tier, subscription_price_id, current_period_end, cancel_at_period_end, trial_started_at, trial_ends_at",
      )
      .eq("id", userId)
      .maybeSingle();

    // Internal test-user and launch-comp bypass: treat as premium regardless of Stripe state.
    const isTest = (profile as { is_test_user?: boolean } | null)?.is_test_user === true;
    const isComp = (profile as { is_launch_comp?: boolean } | null)?.is_launch_comp === true;
    const bypass = isTest || isComp;

    return {
      isPremium: bypass || !!profile?.is_premium,
      status: bypass ? "active" : (profile?.subscription_status ?? null),
      tier: (profile?.subscription_tier as PlanId | null) ?? null,
      priceId: profile?.subscription_price_id ?? null,
      currentPeriodEnd: profile?.current_period_end ?? null,
      cancelAtPeriodEnd: !!profile?.cancel_at_period_end,
      trialStartedAt: profile?.trial_started_at ?? null,
      trialEndsAt: profile?.trial_ends_at ?? null,
    };
  });

// ─── Sync from Stripe (fallback / post-checkout) ────────────────────────────
// Pulls the latest subscription from Stripe and writes it onto the profile.
// Safety net so the success page works even before the webhook fires.
export const syncSubscriptionFromStripe = createServerFn({ method: "POST" })
  .inputValidator((data) =>
    z.object({ accessToken: z.string().min(10) }).parse(data),
  )
  .handler(async ({ data }) => {
    const { authFromToken, getOrCreateCustomerId, stripe, supabaseAdmin } =
      await import("./billing.server");

    const { userId, email } = await authFromToken(data.accessToken);

    // Internal test-user / launch-comp bypass: skip Stripe sync entirely.
    const { data: testCheck } = await supabaseAdmin
      .from("profiles")
      .select("is_test_user, is_launch_comp")
      .eq("id", userId)
      .maybeSingle();
    const tc = testCheck as { is_test_user?: boolean; is_launch_comp?: boolean } | null;
    if (tc?.is_test_user === true || tc?.is_launch_comp === true) {
      return { isPremium: true, tier: null, status: "active" as const };
    }

    const customerId = await getOrCreateCustomerId(userId, email);
    const subs = await stripe().subscriptions.list({
      customer: customerId,
      status: "all",
      limit: 5,
      expand: ["data.items.data.price"],
    });

    // Pick the most recent active-ish subscription
    const ranked = subs.data
      .filter((s) => ["active", "trialing", "past_due"].includes(s.status))
      .sort((a, b) => b.created - a.created);
    const sub = ranked[0] ?? subs.data.sort((a, b) => b.created - a.created)[0] ?? null;

    if (!sub) {
      await supabaseAdmin
        .from("profiles")
        .update({
          is_premium: false,
          subscription_status: null,
          subscription_tier: null,
          subscription_price_id: null,
          current_period_end: null,
          cancel_at_period_end: false,
          plan_tier: "free",
        })
        .eq("id", userId);
      return { isPremium: false };
    }

    const item = sub.items.data[0];
    const priceId = item?.price?.id ?? null;
    const lookupKey = (item?.price as unknown as { lookup_key?: string | null })?.lookup_key ?? null;
    const tier = tierFromPriceId(priceId);
    const planTier = resolveSubscriptionTier(lookupKey, priceId);
    const isPremium = ["active", "trialing"].includes(sub.status);
    const periodEndUnix = (item as unknown as { current_period_end?: number })?.current_period_end
      ?? (sub as unknown as { current_period_end?: number }).current_period_end
      ?? null;

    const planTierForWrite: "free" | "plus" | "premium" =
      ["active", "trialing", "past_due"].includes(sub.status) ? planTier : "free";

    await supabaseAdmin
      .from("profiles")
      .update({
        is_premium: isPremium,
        subscription_status: sub.status,
        subscription_tier: tier,
        subscription_price_id: priceId,
        current_period_end: periodEndUnix
          ? new Date(periodEndUnix * 1000).toISOString()
          : null,
        cancel_at_period_end: !!sub.cancel_at_period_end,
        plan_tier: planTierForWrite,
      })
      .eq("id", userId);


    return { isPremium, tier, status: sub.status };
  });

// ─── Verify Checkout Session ────────────────────────────────────────────────
// Validates that a session_id from the success URL actually belongs to this
// user and represents an active/trialing subscription. Used by the return
// handler to prevent spoofed ?upgrade=success links from showing a fake
// success state in the UI.
//
// Throws a typed error string the client can pattern-match. Errors here are
// considered "verification failures" and should NOT be retried.
export const verifyCheckoutSession = createServerFn({ method: "POST" })
  .inputValidator((data) =>
    z
      .object({
        accessToken: z.string().min(10),
        sessionId: z.string().min(3),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { authFromToken, stripe, supabaseAdmin } = await import("./billing.server");

    const { userId, email } = await authFromToken(data.accessToken);

    let session;
    try {
      session = await stripe().checkout.sessions.retrieve(data.sessionId, {
        expand: ["subscription"],
      });
    } catch (e) {
      throw new Error("VERIFY_FAILED:session_not_found");
    }

    // Match customer to this user. Prefer stripe_customer_id on the profile,
    // fall back to email match for the very first checkout (where we created
    // the Stripe customer just before redirecting).
    const { data: profile } = await supabaseAdmin
      .from("profiles")
      .select("stripe_customer_id, email, normalized_email")
      .eq("id", userId)
      .maybeSingle();

    const sessionCustomerId =
      typeof session.customer === "string"
        ? session.customer
        : session.customer?.id ?? null;

    const customerMatches =
      (profile?.stripe_customer_id &&
        sessionCustomerId === profile.stripe_customer_id) ||
      (!profile?.stripe_customer_id &&
        session.customer_email &&
        email &&
        session.customer_email.toLowerCase() === email.toLowerCase());

    if (!customerMatches) {
      throw new Error("VERIFY_FAILED:customer_mismatch");
    }

    const sub = typeof session.subscription === "string"
      ? null
      : session.subscription;

    if (!sub) {
      // The subscription should exist after a completed checkout. If not, the
      // session may still be processing — surface as a verification miss so
      // the client doesn't show success.
      throw new Error("VERIFY_FAILED:no_subscription");
    }

    if (!["active", "trialing"].includes(sub.status)) {
      throw new Error(`VERIFY_FAILED:status_${sub.status}`);
    }

    return { ok: true as const, status: sub.status };
  });
